/**
 * useNotifications — notification state, polling, and open/close logic (WI-56).
 *
 * Responsibilities:
 *   - Fetch unread count on mount when authenticated.
 *   - Poll unread count every 5 minutes, only when tab is visible.
 *   - Re-fetch immediately when tab becomes visible after being hidden.
 *   - On openDropdown(): fetch notification list + fire markAllRead in parallel,
 *     clear badge optimistically before the server responds.
 *   - Stop all polling on unmount or when isAuthenticated goes false.
 *
 * The hook never conditionally calls React hooks — it always runs the same hooks
 * and gates API calls on the isAuthenticated parameter.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchUnreadCount,
  fetchNotifications,
  markAllRead as apiMarkAllRead,
  type NotificationItem,
} from '../api/notifications';

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

export interface UseNotificationsReturn {
  /** Count driving the bell badge. */
  unreadCount: number;
  /** Notification list, populated when dropdown is open. */
  notifications: NotificationItem[];
  /** True while fetching the notification list. */
  isLoading: boolean;
  /** Whether the dropdown/sheet is currently open. */
  isOpen: boolean;
  /**
   * Open the dropdown: clears badge optimistically, fetches notification list,
   * and fires mark-all-read in the background.
   */
  openDropdown: () => void;
  /** Close the dropdown/sheet. */
  closeDropdown: () => void;
  /**
   * Navigate to the school detail page for a notification and close the dropdown.
   * Score-tier-change: /schools/<slug>?sport=...&target_year=...&position=...
   * New-camp: /schools/<slug>?sport=...
   */
  navigateToNotification: (n: NotificationItem) => void;
}

export function useNotifications(isAuthenticated: boolean): UseNotificationsReturn {
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Keep the latest isAuthenticated value in a ref for use inside interval callbacks.
  const isAuthRef = useRef(isAuthenticated);
  isAuthRef.current = isAuthenticated;

  // Fetch the unread count (lightweight endpoint, used for polling).
  const refreshUnreadCount = useCallback(async () => {
    if (!isAuthRef.current) return;
    try {
      const data = await fetchUnreadCount();
      setUnreadCount(data.count);
    } catch {
      // Swallow errors — the badge will just not update.
    }
  }, []);

  // Initial fetch on mount (when authenticated). Polling setup.
  useEffect(() => {
    if (!isAuthenticated) {
      setUnreadCount(0);
      setNotifications([]);
      setIsOpen(false);
      return;
    }

    // Initial fetch.
    void refreshUnreadCount();

    // 5-minute polling, guarded by document visibility.
    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') {
        void refreshUnreadCount();
      }
    }, POLL_INTERVAL_MS);

    // Catch-up fetch when tab becomes visible.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void refreshUnreadCount();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, refreshUnreadCount]);

  const openDropdown = useCallback(() => {
    if (!isAuthRef.current) return;

    // Optimistic badge clear — happens synchronously before any network call.
    setUnreadCount(0);
    setIsOpen(true);
    setIsLoading(true);

    // Fetch the notification list and fire mark-all-read in parallel.
    Promise.all([
      fetchNotifications(20),
      apiMarkAllRead().catch(() => {
        // Mark-all-read failure is swallowed; badge will reappear on next poll.
      }),
    ])
      .then(([items]) => {
        setNotifications(items);
      })
      .catch(() => {
        setNotifications([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
  }, []);

  const navigateToNotification = useCallback(
    (n: NotificationItem) => {
      setIsOpen(false);

      // new_camp: prefer the camp's registration URL when present, opened in a
      // new tab so the user keeps their place in the app. Hub-link rows that
      // don't carry a per-camp registration URL (camp_registration_url is null)
      // fall back to the school detail page.
      if (n.event_type === 'new_camp' && n.camp_registration_url) {
        window.open(n.camp_registration_url, '_blank', 'noopener,noreferrer');
        return;
      }

      const params = new URLSearchParams();
      if (n.sport_slug) params.set('sport', n.sport_slug);
      if (n.event_type === 'score_tier_changed') {
        if (n.target_year !== null) params.set('target_year', String(n.target_year));
        if (n.position_slug) params.set('position', n.position_slug);
      }
      const query = params.toString();
      navigate(`/schools/${n.school_slug}${query ? `?${query}` : ''}`);
    },
    [navigate]
  );

  return {
    unreadCount,
    notifications,
    isLoading,
    isOpen,
    openDropdown,
    closeDropdown,
    navigateToNotification,
  };
}
